import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react"
import { Row, Col, Spin, LucideIcon, message, Button, jsonToQueryString, useCRUDController, Modal } from './ot'
import { useNavigate, useSearchParams, } from "react-router-dom";
import BaseDocumentResponseCanvasEdit from './base_document_response_canvas_edit'
import dayjs from "dayjs";


const BaseCashRealTimeModal = ({
    setOpen = () => { },
    open = false,
    title = "即時詳細資訊",
    width = "1200px",
    record,               // ⭐ record.document_id 在這裡
    realTimeApiRoute,     // ⭐ 你傳的 API 在這裡
    axiosMode = "get",
    documentApiRoute = '/cash_management/backend/record_document/view',
    onFinish = ({ axiosMode, status, values }) => { },
}) => {

    const documentId = record?.document_id || null
    const [loading, setLoading] = useState(false)
    const [transactions, setTransactions] = useState([]);


    const modalRender_ = () => {
        // 交易明細資料（示例資料，可改為 API 串接）
        const transactions = [
            { id: 'TX202510120001', category: '空間預約', orderNo: '訂單 #51001', customer: '王小明', channel: '信用卡', amount: 12500.0, date: '2025-10-12 14:23:10', status: '成功', progress: 100 },
            { id: 'TX202510120002', category: '停車位預約', orderNo: '訂單 #51002', customer: '林美玉', channel: '第三方支付', amount: 2899.0, date: '2025-10-12 14:21:08', status: '處理中', progress: 65 },
            { id: 'TX202510120003', category: '空間預約', orderNo: '訂單 #51003', customer: '陳大同', channel: '銀行轉帳', amount: 78000.5, date: '2025-10-12 13:58:45', status: '失敗', progress: 100 },
            { id: 'TX202510110987', category: '停車位預約', orderNo: '訂單 #50987', customer: '李麗華', channel: '電子錢包', amount: 15880.0, date: '2025-10-11 19:03:12', status: '退款處理', progress: 40 },
            { id: 'TX202510110912', category: '空間預約', orderNo: '訂單 #50912', customer: '周家豪', channel: '信用卡', amount: 3600.0, date: '2025-10-11 17:36:49', status: '成功', progress: 100 },
        ]

        const statusStyle = (status) => {
            switch (status) {
                case '成功':
                    return { badge: 'bg-green-100 text-green-700', bar: 'bg-green-500' }
                case '處理中':
                    return { badge: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500' }
                case '失敗':
                    return { badge: 'bg-red-100 text-red-700', bar: 'bg-red-500' }
                case '退款處理':
                    return { badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' }
                default:
                    return { badge: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400' }
            }
        }

        // 彙總資訊
        const totalCount = transactions.length
        const totalAmount = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
        const successCount = transactions.filter(t => t.status === '成功').length
        const successRate = totalCount ? ((successCount / totalCount) * 100).toFixed(1) : '0.0'
        const lastUpdate = dayjs().format('YYYY-MM-DD HH:mm:ss')

        return (
            <>
                <div className="space-y-6">
                    {/* 彙總資訊 */}
                    <div className="bg-muted rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-foreground">交易明細彙總</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                                {/* <Clock className="h-3 w-3" /> */}
                                最後更新: {lastUpdate}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-xl font-bold text-blue-600">{totalCount.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">筆數</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl font-bold text-green-600">${totalAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</div>
                                <div className="text-xs text-muted-foreground">總金額</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl font-bold text-emerald-600">{successRate}%</div>
                                <div className="text-xs text-muted-foreground">成功率</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl font-bold text-purple-600">{successCount.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">成功筆數</div>
                            </div>
                        </div>
                    </div>

                    {/* 交易明細列表 */}
                    <div className="bg-muted rounded-lg p-3 space-y-2">
                        <div className="text-sm font-medium text-foreground mb-2">最新交易</div>
                        <div className="space-y-2">
                            {transactions.map((t) => {
                                const styles = statusStyle(t.status)
                                return (
                                    <div key={t.id} className="p-3 bg-white rounded border border-gray-100">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                            {/* 左側：交易項目與訂單/ID */}
                                            <div className="min-w-[220px]">
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${t.category === '空間預約' ? 'bg-sky-100 text-sky-700' : 'bg-teal-100 text-teal-700'}`}>{t.category}</span>
                                                    <span className="text-xs text-muted-foreground">{t.orderNo}</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground">{t.id}</div>
                                            </div>

                                            {/* 中間：金額、日期、渠道、交易人 */}
                                            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <div className="text-sm text-muted-foreground">交易金額</div>
                                                    <div className="text-sm font-semibold text-green-600">${Number(t.amount).toLocaleString('zh-TW', { minimumFractionDigits: 2 })}</div>
                                                </div>
                                                <div>
                                                    <div className="text-sm text-muted-foreground">交易日期</div>
                                                    <div className="text-sm font-medium">{dayjs(t.date).format('YYYY-MM-DD HH:mm:ss')}</div>
                                                </div>
                                                <div>
                                                    <div className="text-sm text-muted-foreground">交易渠道</div>
                                                    <div className="text-sm font-medium">
                                                        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs">{t.channel}</span>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-sm text-muted-foreground">交易人</div>
                                                    <div className="text-sm font-medium">
                                                        <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 text-xs">{t.customer}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 右側：狀態與進度 */}
                                            <div className="md:w-[240px]">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`px-2 py-1 rounded-full text-xs ${styles.badge}`}>{t.status}</span>
                                                    <span className="text-xs text-muted-foreground">{t.progress}%</span>
                                                </div>
                                                <div className="h-2 w-full bg-gray-100 rounded">
                                                    <div
                                                        className={`h-2 rounded ${styles.bar}`}
                                                        style={{ width: `${Math.min(Math.max(t.progress, 0), 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </>
        );
    }
    const modalRender = () => {
        const statusStyle = (status) => {
            switch (status) {
                case '成功':
                    return { badge: 'bg-green-100 text-green-700', bar: 'bg-green-500' }
                case '處理中':
                    return { badge: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500' }
                case '失敗':
                    return { badge: 'bg-red-100 text-red-700', bar: 'bg-red-500' }
                case '退款處理':
                    return { badge: 'bg-amber-100 text-amber-700', bar: 'bg-amber-500' }
                default:
                    return { badge: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400' }
            }
        }

        // ⭐ 改成使用 API 轉換後資料
        const tx = transactions.filter(t => t.refund === "否"); // 過濾掉空物件

        //  彙總資訊
        const totalCount = tx.length;
        const totalAmount = tx.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);
        const successCount = tx.filter(t => t.status === '成功').length;
        const successRate = totalCount ? ((successCount / totalCount) * 100).toFixed(1) : '0.0';
        const lastUpdate = dayjs().format('YYYY-MM-DD HH:mm:ss');

        return (
            <>
                <div className="space-y-6">

                    {/* 彙總資訊 */}
                    <div className="bg-muted rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-foreground">交易明細彙總</span>
                            <span className="text-xs text-muted-foreground">最後更新: {lastUpdate}</span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="text-center">
                                <div className="text-xl font-bold text-blue-600">{totalCount.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">筆數</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl font-bold text-green-600">
                                    ${totalAmount.toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                                </div>
                                <div className="text-xs text-muted-foreground">總金額</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl font-bold text-emerald-600">{successRate}%</div>
                                <div className="text-xs text-muted-foreground">成功率</div>
                            </div>
                            <div className="text-center">
                                <div className="text-xl font-bold text-purple-600">{successCount.toLocaleString()}</div>
                                <div className="text-xs text-muted-foreground">成功筆數</div>
                            </div>
                        </div>
                    </div>

                    {/* 最新交易列表 */}
                    <div className="bg-muted rounded-lg p-3 space-y-2">
                        <div className="text-sm font-medium text-foreground mb-2">最新交易</div>

                        <div className="space-y-2">
                        {tx.map(t => {
                            const styles = statusStyle(t.status);

                            return (
                                <div key={t.id} className="p-3 bg-white rounded border border-gray-100">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">

                                        {/* 左側 */}
                                        <div className="min-w-[220px]">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className={`px-2 py-0.5 rounded-full text-xs ${t.category === '空間預約' ? 'bg-sky-100 text-sky-700' : 'bg-teal-100 text-teal-700'}`}>{t.category}</span>
                                                <span className="text-xs text-muted-foreground">{t.orderNo}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground">{t.id}</div>
                                        </div>

                                        {/* 中間欄位 */}
                                        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                                            <div>
                                                <div className="text-sm text-muted-foreground">交易金額</div>
                                                <div className="text-sm font-semibold text-green-600">
                                                    ${Number(t.amount).toLocaleString('zh-TW', { minimumFractionDigits: 2 })}
                                                </div>
                                            </div>

                                            <div>
                                                <div className="text-sm text-muted-foreground">交易日期</div>
                                                <div className="text-sm font-medium">{dayjs(t.date).format("YYYY-MM-DD HH:mm:ss")}</div>
                                            </div>

                                            <div>
                                                <div className="text-sm text-muted-foreground">交易渠道</div>
                                                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs">
                                                    {t.channel}
                                                </span>
                                            </div>

                                            <div>
                                                <div className="text-sm text-muted-foreground">交易人</div>
                                                <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 text-xs">
                                                    {t.customer}
                                                </span>
                                            </div>
                                        </div>

                                        {/* 右側狀態列 */}
                                        <div className="md:w-[240px]">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className={`px-2 py-1 rounded-full text-xs ${styles.badge}`}>{t.status}</span>
                                                <span className="text-xs text-muted-foreground">{t.progress}%</span>
                                            </div>
                                            <div className="h-2 w-full bg-gray-100 rounded">
                                                <div
                                                    className={`h-2 rounded ${styles.bar}`}
                                                    style={{ width: `${Math.min(Math.max(t.progress, 0), 100)}%` }}
                                                />
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    </div>

                </div>
            </>
        );
    };


    const convertApiDataToTransactions = (rows) => {
        return rows.map((res) => {
            let obj = {};

            // 將 API 的欄位扁平化成 { "交易金額": "12500", "交易日期": "...", ... }
            res?.response_form_value_data?.forEach((item) => {
                const code = item?.form_column_form_value_data?.[0]?.code;
                const value = item?.value?.value;
                if (code) {
                    obj[code] = value;
                }
            });

            return {
                id: "TX" + (obj["訂單"] || ""),         // 你可改成真正的 ID
                category: obj["交易渠道"]?.includes("停車") ? "停車位預約" : "空間預約",
                orderNo: "訂單 #" + obj["訂單"],
                customer: obj["交易人"] || "",
                channel: obj["交易渠道"] || "",
                amount: Number(obj["交易金額"] || 0),
                date: obj["交易日期"] || "",
                status: obj["狀態"] || "",
                refund: obj["是否退款"] || "否",
                progress: obj["狀態"] === "處理中" ? 65 : 100, // 假設處理中=65%，其他=100%
            };
        });
    };


    const getDocumentData = () => {
        if (!realTimeApiRoute || !documentId) return;

        documentController
            .getData({ document_id: documentId })
            .then((response) => {
                let data = response?.data
                let dataFirst = data.find((_) => true)
                console.log(dataFirst)

                const rows = response?.data || [];
                const tx = convertApiDataToTransactions(rows);
                setTransactions(tx);
                console.log("🔥 轉換後交易資料：", tx);

            })
    }
    const documentController = useCRUDController({
        // apiRoute: documentApiRoute,
        apiRoute: realTimeApiRoute,
        onBefore: ({ axiosMode }) => {
            if (axiosMode !== "get") {
                setLoading(true);
            }
        },
        onFinish: ({ axiosMode, status, values }) => {
            if (status === "success") {
                if (axiosMode === "post") {
                    message.success("新增成功")
                    // setReloadAndResetCount((pre) => pre + 1)
                } else if (axiosMode === "patch") {
                    message.success("修改成功")
                    // setReloadCount((pre) => pre + 1)
                }
                else if (axiosMode === "delete") {
                    message.success("刪除成功")
                    // setReloadAndResetCount((pre) => pre + 1)

                }
            }
            else if (status === "error") {
                if (axiosMode === "post") {
                    message.error("新增失敗")
                    // setReloadAndResetCount((pre) => pre + 1)

                }
                else if (axiosMode === "patch") {
                    message.error("修改失敗")
                    // setReloadCount((pre) => pre + 1)
                }
                else if (axiosMode === "delete") {
                    message.error("刪除失敗")
                    // setReloadAndResetCount((pre) => pre + 1)
                }
            }
            if (axiosMode !== "get") {
                // setLoading(false)
                // setModalOpen(false);
            }
        }
    })

    useEffect(() => {
        getDocumentData()
    }, [])
    useEffect(() => {
        if (open && documentId) {
            getDocumentData();
        }
    }, [open, documentId]);



    return (<>
        <Modal
            confirmLoading={loading}
            title={title}
            open={open}
            // onCancel={onCancel}
            onClose={() => {
                setOpen(false)
            }}
            close={true}
            okProps={{}}
            cancelText="取消"
            closeText="關閉"
            width={width}
        >
            {modalRender()}
        </Modal>
    </>)
}
export default BaseCashRealTimeModal
